'use client';

import Link from 'next/link';
import { type FC, useEffect, useRef } from 'react';

import { type NewsCategory, type NewsListCategory, type NewsLang } from '../../services/generated/Api';
import { newsListHref } from '../../lib/newsPath';

interface NewsCategoryListProps {
  activeCategory: NewsCategory | null;
  allLabel: string;
  ariaLabel: string;
  categories: NewsListCategory[];
  lang: NewsLang;
}

export const NewsCategoryList: FC<NewsCategoryListProps> = ({
  activeCategory,
  allLabel,
  ariaLabel,
  categories,
  lang,
}) => {
  const activeRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
  }, [activeCategory]);

  const options: Array<{ category: NewsCategory | null; name: string }> = [
    { category: null, name: allLabel },
    ...categories,
  ];

  return (
    <nav
      aria-label={ariaLabel}
      className="hide-scrollbar -mx-4 flex max-w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:max-w-full md:px-0"
    >
      {options.map((option) => {
        const active = option.category === activeCategory;
        return (
          <Link
            key={option.category ?? 'all'}
            ref={active ? activeRef : undefined}
            href={newsListHref(lang, undefined, option.category)}
            prefetch={false}
            aria-current={active ? 'page' : undefined}
            className={`inline-flex min-h-11 shrink-0 items-center justify-center rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-bg-primary md:text-base ${
              active
                ? 'border-accent-blue bg-accent-blue text-white shadow-sm ring-2 ring-accent-blue/30 ring-offset-2 ring-offset-bg-primary'
                : 'border-separator bg-bg-secondary text-label-primary hover:bg-bg-tertiary'
            }`}
          >
            {option.name}
          </Link>
        );
      })}
    </nav>
  );
};
